// src/pages/AddExamQuestions.js
import React, { useState, useEffect, useRef } from "react";
import { coursesAPI, examsAPI } from "../services/api";
import { useNavigate } from "react-router-dom";
import "../styles/AddExamQuestions.css";
import {
  FaPlus,
  FaTrash,
  FaCheck,
  FaListUl,
  FaSave,
  FaArrowRight,
  FaArrowLeft,
  FaClock,
  FaInfoCircle,
  FaAlignLeft,
  FaTimes,
  FaEdit,
  FaFileAlt,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

const AddExamQuestions = () => {
  const navigate = useNavigate();

  // Steps
  const [step, setStep] = useState(1);

  // Data loading
  const [courses, setCourses] = useState([]);
  const [teacherCredits, setTeacherCredits] = useState(0);
  const [loading, setLoading] = useState(true);

  // Exam Settings
  const [examData, setExamData] = useState({
    courseId: "",
    title: "",
    description: "",
    duration: 60,
    isMandatory: false,
    allowRetake: false,
    maxAttempts: 1,
    showResultsImmediately: true,
  });

  // Questions
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  // Mobile detection (live)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Full-screen editor modal for mobile
  const [editorOpen, setEditorOpen] = useState(false);

  // Add-question type picker modal
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  // Import from Word modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const importFileRef = useRef(null);

  const selectedQuestion = questions.find((q) => q.id === selectedQuestionId) || null;

  // Load courses and Teacher Profile
  useEffect(() => {
    const fetchCoursesAndProfile = async () => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      try {
        // Fetch courses
        const response = await coursesAPI.getAll();
        setCourses(response.data || []);
        
        // Fetch teacher AI credits
        const profileRes = await fetch("https://api.daress.app/api/auth/me", {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const profileData = await profileRes.json();
        if (profileRes.ok && profileData?.user?.ai_credits !== undefined) {
          // You need to import this state at the top, or I'll add it in the next chunk
          setTeacherCredits(profileData.user.ai_credits);
        }
      } catch (error) {
        toast.error("❌ فشل تحميل البيانات المبدئية");
      } finally {
        setLoading(false);
      }
    };

    fetchCoursesAndProfile();
  }, [navigate]);

  // If step 2 & no selected question, select first for convenience (but do NOT open editor)
  useEffect(() => {
    if (step === 2 && !selectedQuestionId && questions.length > 0) {
      setSelectedQuestionId(questions[0].id);
    }
  }, [step, selectedQuestionId, questions.length]);

  const handleDetailChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = ["duration", "maxAttempts"];
    const nextValue =
      type === "checkbox" ? checked : numericFields.includes(name) ? Number(value) : value;

    setExamData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const validateStep1 = () => {
    if (!examData.courseId) {
      toast.warn("⚠️ يرجى اختيار الدورة التدريبية");
      return false;
    }
    if (!examData.title.trim()) {
      toast.warn("⚠️ يرجى إدخال عنوان الامتحان");
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) setStep(2);
  };

  // -------------------- Questions --------------------

  const buildQuestionByType = (type) => {
    const baseQuestion = {
      id: Date.now(),
      text: "",
      type,
      points: 1,
      options: [],
    };

    if (type === "mcq") {
      baseQuestion.options = [
        { text: "", isCorrect: true }, // Default to first option correct
        { text: "", isCorrect: false },
      ];
    } else if (type === "true_false") {
      baseQuestion.options = [
        { text: "صح (True)", isCorrect: true },
        { text: "خطأ (False)", isCorrect: false },
      ];
    } else if (type === "matching") {
      baseQuestion.options = [{ text: "", matchText: "" }];
    } else if (type === "ordering") {
      baseQuestion.options = [{ text: "", orderIndex: 1 }];
    } else if (type === "essay") {
       // Essay default structure
       baseQuestion.useAIGrading = false; // New property
    }

    return baseQuestion;
  };

  // Open type picker
  const openAddQuestion = () => {
    setTypePickerOpen(true);
  };

  const addQuestion = (type) => {
    const newQ = buildQuestionByType(type);

    setQuestions((prev) => [...prev, newQ]);
    setSelectedQuestionId(newQ.id);

    setTypePickerOpen(false);

    // On mobile: open editor immediately in full-screen modal
    if (isMobile) setEditorOpen(true);
  };

  const updateQuestion = (id, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q;
        if (field === "points") return { ...q, points: Number(value) || 1 };
        return { ...q, [field]: value };
      })
    );
  };

  const removeQuestion = (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السؤال؟")) return;

    setQuestions((prev) => {
      const newQuestions = prev.filter((q) => q.id !== id);

      if (selectedQuestionId === id) {
        const nextSelected = newQuestions.length > 0 ? newQuestions[0].id : null;
        setSelectedQuestionId(nextSelected);

        // If deleted while editor open
        if (isMobile) setEditorOpen(false);
      }

      return newQuestions;
    });
  };

  // Options
  const updateOption = (qId, idx, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q;
        const newOptions = [...q.options];
        newOptions[idx] = { ...newOptions[idx], [field]: value };
        return { ...q, options: newOptions };
      })
    );
  };

  const setCorrectOption = (qId, idx) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q;
        const newOptions = q.options.map((opt, i) => ({ ...opt, isCorrect: i === idx }));
        return { ...q, options: newOptions };
      })
    );
  };

  const addOption = (qId) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q;

        let newOpt = { text: "", isCorrect: false };
        if (q.type === "matching") newOpt = { text: "", matchText: "" };
        if (q.type === "ordering") newOpt = { text: "", orderIndex: q.options.length + 1 };

        return { ...q, options: [...q.options, newOpt] };
      })
    );
  };

  const openEditorFor = (id) => {
    setSelectedQuestionId(id);
    if (isMobile) setEditorOpen(true);
  };

  const closeEditor = () => setEditorOpen(false);

  // -------------------- Word Import --------------------

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportLoading(false);
    setImportPreview(null);
  };

  const handleWordFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setImportFile(file);
  };

  const handleWordImportSubmit = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const res = await examsAPI.importWordPreview(importFile);
      setImportPreview(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || 'حدث خطأ أثناء تحليل الملف';
      toast.error(`❌ ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const convertPreviewQuestion = (previewQ, index) => {
    const base = {
      id: Date.now() + index * 13,
      text: previewQ.questionText,
      type: previewQ.type,
      points: previewQ.points || 1,
      options: [],
    };

    if (previewQ.type === 'mcq') {
      base.options = (previewQ.options || []).map((opt) => ({
        text: opt.text,
        isCorrect: opt.key?.toUpperCase() === previewQ.correctAnswer?.toUpperCase(),
      }));
      if (base.options.length > 0 && !base.options.some((o) => o.isCorrect)) {
        base.options[0].isCorrect = true;
      }
    } else if (previewQ.type === 'true_false') {
      const ca = (previewQ.correctAnswer || '').toLowerCase();
      const trueCorrect = ['true', 'صح'].includes(ca);
      base.options = [
        { text: 'صح (True)', isCorrect: trueCorrect },
        { text: 'خطأ (False)', isCorrect: !trueCorrect },
      ];
    } else if (previewQ.type === 'fill_blank') {
      if (previewQ.correctAnswer) {
        base.options = [{ text: previewQ.correctAnswer, isCorrect: true }];
      }
    }

    return base;
  };

  const applyImportedQuestions = () => {
    if (!importPreview) return;
    const importable = importPreview.questions.filter((q) => q.status !== 'failed');
    const converted = importable.map((q, i) => convertPreviewQuestion(q, i));
    setQuestions((prev) => [...prev, ...converted]);
    if (converted.length > 0) setSelectedQuestionId(converted[0].id);
    closeImportModal();
    toast.success(`✅ تم إضافة ${converted.length} سؤال من الملف`);
  };

  const handleSubmit = async () => {
    if (questions.length === 0) {
      toast.warn("⚠️ يجب إضافة سؤال واحد على الأقل");
      return;
    }

    // Validation
    for (const [idx, q] of questions.entries()) {
      if (!q.text || !q.text.trim()) {
         toast.warn(`⚠️ السؤال رقم ${idx + 1} ليس له نص`);
         return;
      }
      if (q.type === "mcq" || q.type === "true_false") {
        const hasCorrect = q.options.some(opt => opt.isCorrect);
        if (!hasCorrect) {
          toast.warn(`⚠️ السؤال رقم ${idx + 1} (${typeLabel(q.type)}) ليس له إجابة صحيحة محددة`);
          return;
        }
      }
    }

    try {
      const createResponse = await examsAPI.create(examData);
      const examId = createResponse.data.examId;

      const formattedQuestions = questions.map((q) => ({
        text: q.text?.trim() ? q.text : "سؤال جديد",
        type: q.type,
        points: Number(q.points) || 1,
        options: q.options || [],
        useAIGrading: q.useAIGrading ? true : false,
      }));

      await examsAPI.addQuestions(examId, formattedQuestions);

      toast.success("✅ تم إنشاء الامتحان بنجاح!");
      navigate("/exams");
    } catch (error) {
      console.error(error);
      toast.error("❌ حدث خطأ أثناء الحفظ");
    }
  };

  const typeLabel = (t) => {
    if (t === "mcq") return "اختيارات";
    if (t === "true_false") return "صح/خطأ";
    if (t === "fill_blank") return "أكمل";
    if (t === "essay") return "مقالي";
    return t;
  };

  if (loading) return <div className="loading-spinner">⏳ جارٍ التحميل...</div>;

  // Editor content (shared between desktop panel and mobile modal)
  const renderEditorContent = () => {
    if (!selectedQuestion) {
      if (questions.length === 0) {
        return (
          <div className="import-start-cta">
            <h3>ابدأ بإضافة أسئلتك</h3>
            <p>اختر طريقة لإضافة الأسئلة إلى الامتحان</p>
            <div className="import-cta-options">
              <button className="cta-option" onClick={openAddQuestion} type="button">
                <FaPlus size={26} />
                <strong>إضافة سؤال يدوياً</strong>
                <span>أضف أسئلة خطوة بخطوة</span>
              </button>
              <button className="cta-option cta-import" onClick={() => setImportModalOpen(true)} type="button">
                <FaFileAlt size={26} />
                <strong>استيراد من Word</strong>
                <span>حمّل ملف .docx وسنستخرج الأسئلة تلقائياً</span>
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="empty-state">
          <FaPlus size={40} style={{ marginBottom: "16px" }} />
          <h3 style={{ margin: 0 }}>اختر سؤالًا أو أضف سؤالًا للبدء</h3>
          <p style={{ marginTop: "10px" }}>
            اضغط على زر "تعديل" بجانب السؤال، أو أضف سؤالًا جديدًا.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="editor-header">
          {isMobile ? (
            <button className="modal-close-btn" onClick={closeEditor} type="button">
              <FaTimes /> إغلاق
            </button>
          ) : (
            <div />
          )}

          <h3 style={{ margin: 0 }}>تعديل السؤال</h3>

          <div className="points-wrapper">
            <span>الدرجة:</span>
            <input
              type="number"
              className="form-control"
              style={{ width: "90px", padding: "0.5rem" }}
              value={selectedQuestion.points}
              onChange={(e) => updateQuestion(selectedQuestion.id, "points", e.target.value)}
              min={1}
            />
          </div>
        </div>

        <div className="form-group">
          <label>نوع السؤال</label>
          <div className="question-type-chip">{typeLabel(selectedQuestion.type)}</div>
        </div>

        <div className="form-group">
          <label>نص السؤال</label>
          <textarea
            className="question-text-area"
            value={selectedQuestion.text}
            onChange={(e) => updateQuestion(selectedQuestion.id, "text", e.target.value)}
            placeholder="اكتب نص السؤال هنا..."
          />
        </div>

        <div className="options-container">
          <h4 style={{ marginTop: 0 }}>الخيارات / الإجابات</h4>

          {/* MCQ */}
          {selectedQuestion.type === "mcq" && (
            <div>
              {selectedQuestion.options.map((opt, idx) => (
                <div key={idx} className={`option-row ${opt.isCorrect ? "correct" : ""}`}>
                  <input
                    type="radio"
                    checked={!!opt.isCorrect}
                    onChange={() => setCorrectOption(selectedQuestion.id, idx)}
                  />
                  <input
                    type="text"
                    className="option-input"
                    value={opt.text}
                    onChange={(e) => updateOption(selectedQuestion.id, idx, "text", e.target.value)}
                    placeholder={`الخيار ${idx + 1}`}
                  />
                </div>
              ))}

              <button
                className="add-q-btn"
                onClick={() => addOption(selectedQuestion.id)}
                type="button"
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                + إضافة خيار
              </button>
            </div>
          )}

          {/* True/False */}
          {selectedQuestion.type === "true_false" && (
            <div>
              {selectedQuestion.options.map((opt, idx) => (
                <div key={idx} className={`option-row ${opt.isCorrect ? "correct" : ""}`}>
                  <input
                    type="radio"
                    checked={!!opt.isCorrect}
                    onChange={() => setCorrectOption(selectedQuestion.id, idx)}
                  />
                  <span className="tf-text">{opt.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Fill blank / Essay */}
          {selectedQuestion.type === "fill_blank" && (
            <div className="alert-box" style={{ display: "flex", gap: "10px" }}>
              <FaInfoCircle />
              <p style={{ margin: 0, fontSize: "0.95rem" }}>
                لإضافة فراغ، استخدم القوسين [مثال] في نص السؤال.
              </p>
            </div>
          )}

          {selectedQuestion.type === "essay" && (
            <div className="alert-box" style={{ display: "flex", flexDirection: "column", gap: "10px", backgroundColor: selectedQuestion.useAIGrading ? "#f0fdf4" : "#fff8e6", borderColor: selectedQuestion.useAIGrading ? "#86efac" : "#fde047" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FaInfoCircle style={{ color: selectedQuestion.useAIGrading ? "#16a34a" : "#ca8a04" }} />
                  <strong style={{ color: selectedQuestion.useAIGrading ? "#16a34a" : "#ca8a04" }}>
                    تصحيح الذكاء الاصطناعي
                  </strong>
                </div>
                
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={!!selectedQuestion.useAIGrading}
                    onChange={(e) => {
                      updateQuestion(selectedQuestion.id, "useAIGrading", e.target.checked);
                    }}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
              
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#4b5563" }}>
                {selectedQuestion.useAIGrading 
                  ? "سيتم تصحيح هذا السؤال تلقائياً باستخدام الذكاء الاصطناعي."
                  : "سيتطلب هذا السؤال مراجعة يدوية من قبلك بعد تسليم الطالب للامتحان."}
              </p>
            </div>
          )}
        </div>

        {isMobile && (
          <div className="modal-footer">
            <button className="modal-danger" onClick={() => removeQuestion(selectedQuestion.id)} type="button">
              <FaTrash /> حذف السؤال
            </button>
            <button className="modal-primary" onClick={closeEditor} type="button">
              تم
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="add-exam-container fade-in">
      {/* Top Header Row with AI Credits */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "10px" }}>
         <h2 style={{ margin: 0 }}>📝 إضافة امتحان جديد</h2>
         <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white", padding: "8px 16px", borderRadius: "50px", fontSize: "1rem", fontWeight: "bold", boxShadow: "0 4px 10px rgba(16,185,129,0.3)" }}>
            <span style={{ fontSize: "1.2rem" }}>✨</span>
            <span>نقاط AI المتاحة:</span>
            <span style={{ background: "rgba(255,255,255,0.2)", padding: "2px 8px", borderRadius: "10px" }}>{teacherCredits}</span>
         </div>
      </div>

      {/* Wizard Header */}
      <div className="wizard-header">
        <div className={`step ${step >= 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}>
          <div className="step-num">{step > 1 ? "✓" : "1"}</div>
          <span>بيانات الامتحان</span>
        </div>

        <div className={`step ${step === 2 ? "active" : ""}`}>
          <div className="step-num">2</div>
          <span>إعداد الأسئلة</span>
        </div>
      </div>

      <div className="wizard-content">
        {/* Step 1 */}
        {step === 1 && (
          <div className="exam-details-card fade-in">
            <h2 style={{ marginBottom: "20px" }}>📝 إعدادات الامتحان الأساسية</h2>

            <div className="form-grid">
              <div className="form-group">
                <label>الدورة التدريبية</label>
                <select name="courseId" value={examData.courseId} onChange={handleDetailChange} className="form-control">
                  <option value="">-- اختر الدورة --</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>عنوان الامتحان</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={examData.title}
                  onChange={handleDetailChange}
                  placeholder="مثال: الاختبار النصفي"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaClock /> المدة (دقيقة)
                </label>
                <input
                  type="number"
                  name="duration"
                  className="form-control"
                  value={examData.duration}
                  onChange={handleDetailChange}
                  min={1}
                />
              </div>

              <div className="form-group full-width">
                <label>وصف الامتحان</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows="3"
                  value={examData.description}
                  onChange={handleDetailChange}
                  placeholder="تعليمات للطالب قبل البدء..."
                />
              </div>
              
              {/* Toggles */}
              <div className="form-group full-width" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                <div className="aex-switch-container" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      name="allowRetake"
                      checked={examData.allowRetake}
                      onChange={handleDetailChange}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ fontWeight: 'bold' }}>السماح للطالب بإعادة الامتحان إذا رسب</span>
                </div>

                <div className="aex-switch-container" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      name="showResultsImmediately"
                      checked={examData.showResultsImmediately}
                      onChange={handleDetailChange}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ fontWeight: 'bold' }}>إظهار النتيجة للطالب فور الانتهاء</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "20px", textAlign: "left" }}>
              <button className="btn-finish" onClick={handleNextStep} type="button">
                التالي: الأسئلة <FaArrowLeft />
              </button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="exam-builder-layout fade-in">
            {/* Sidebar: Questions */}
            <div className="questions-sidebar">
              <div className="sidebar-header">
                <h3 style={{ margin: 0 }}>📌 الأسئلة ({questions.length})</h3>

                <button className="add-q-btn add-q-btn-compact" onClick={openAddQuestion} type="button">
                  <FaPlus /> إضافة
                </button>
              </div>

              <div className="questions-list">
                {questions.map((q, idx) => (
                  <div
                    key={q.id}
                    className={`question-item ${selectedQuestionId === q.id ? "active" : ""}`}
                    onClick={() => setSelectedQuestionId(q.id)} // فقط تحديد
                    role="button"
                    tabIndex={0}
                  >
                    <div className="q-item-content">
                      <div className="q-item-title">{q.text || `سؤال ${idx + 1}`}</div>
                      <div className="q-item-meta">
                        <span className="q-type">{typeLabel(q.type)}</span>
                        <span>{q.points} درجة</span>
                      </div>
                    </div>

                    <div className="q-item-actions">
                      <button
                        className="btn-icon edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditorFor(q.id);
                        }}
                        aria-label="تعديل السؤال"
                        type="button"
                        title="تعديل"
                      >
                        <FaEdit />
                      </button>

                      <button
                        className="btn-icon delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeQuestion(q.id);
                        }}
                        aria-label="حذف السؤال"
                        type="button"
                        title="حذف"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}

                {questions.length === 0 && (
                  <div style={{ padding: "0.75rem", color: "var(--text-secondary)" }}>
                    لم تتم إضافة أسئلة بعد. اضغط “إضافة”.
                  </div>
                )}
              </div>

              {/* Desktop add buttons */}
              {!isMobile && (
                <div className="sidebar-footer">
                  <button onClick={() => addQuestion("mcq")} className="add-q-btn" type="button">
                    <FaListUl /> اختيارات
                  </button>
                  <button onClick={() => addQuestion("true_false")} className="add-q-btn" type="button">
                    <FaCheck /> صواب/خطأ
                  </button>
                  <button onClick={() => addQuestion("fill_blank")} className="add-q-btn" type="button">
                    <FaAlignLeft /> أكمل
                  </button>
                  <button onClick={() => addQuestion("essay")} className="add-q-btn" type="button">
                    <FaAlignLeft /> مقالي
                  </button>
                  <button onClick={() => setImportModalOpen(true)} className="btn-import-word" type="button">
                    <FaFileAlt /> استيراد من Word
                  </button>
                </div>
              )}
            </div>

            {/* Desktop Editor Panel only */}
            {!isMobile && (
              <div className="question-editor-panel">
                {renderEditorContent()}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="btn-bottom-actions">
              <button className="btn-prev" onClick={() => setStep(1)} type="button">
                <FaArrowRight /> البيانات الأساسية
              </button>

              <button className="btn-finish" onClick={handleSubmit} type="button">
                <FaSave /> حفظ ونشر الامتحان
              </button>
            </div>

            {/* Mobile: Fullscreen Editor Modal */}
            {isMobile && editorOpen && (
              <div className="modal-overlay" role="dialog" aria-modal="true">
                <div className="modal-fullscreen">
                  {renderEditorContent()}
                </div>
              </div>
            )}

            {/* Import Word Modal */}
            {importModalOpen && (
              <div
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                style={{ alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
              >
                <div className="import-modal">
                  {/* Header */}
                  <div className="import-modal-header">
                    <h3>📄 استيراد أسئلة من ملف Word</h3>
                    <button className="sheet-close" onClick={closeImportModal} type="button">
                      <FaTimes />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="import-modal-body">
                    {!importPreview ? (
                      <div
                        className={`import-dropzone ${importFile ? 'has-file' : ''}`}
                        onClick={() => importFileRef.current?.click()}
                      >
                        <FaUpload size={32} />
                        {importFile ? (
                          <>
                            <strong>{importFile.name}</strong>
                            <span>اضغط لتغيير الملف</span>
                          </>
                        ) : (
                          <>
                            <strong>اسحب الملف هنا أو اضغط للاختيار</strong>
                            <span>يقبل ملفات .docx فقط — الحجم الأقصى 10 MB</span>
                          </>
                        )}
                        <input
                          ref={importFileRef}
                          type="file"
                          accept=".docx"
                          style={{ display: 'none' }}
                          onChange={handleWordFileChange}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="import-summary-bar">
                          <span className="pattern-badge">
                            {importPreview.patternDetected === 'arabic_classic' && '🔤 نمط عربي'}
                            {importPreview.patternDetected === 'english_classic' && '🔡 نمط إنجليزي'}
                            {importPreview.patternDetected === 'structured' && '🧩 نمط منظم'}
                            {importPreview.patternDetected === 'unknown' && '❓ نمط غير معروف'}
                          </span>
                          <span className="badge-ready">✅ جاهز: {importPreview.summary.ready}</span>
                          <span className="badge-review">⚠️ يحتاج مراجعة: {importPreview.summary.needsReview}</span>
                          {importPreview.summary.failed > 0 && (
                            <span className="badge-failed">❌ فشل: {importPreview.summary.failed}</span>
                          )}
                        </div>

                        <div className="import-preview-list">
                          {importPreview.questions.map((q, idx) => (
                            <div key={q.tempId} className={`import-q-item status-${q.status}`}>
                              <div className="import-q-header">
                                <span className="import-q-num">{idx + 1}.</span>
                                <span className="import-q-text">{q.questionText || '(لا يوجد نص)'}</span>
                                <span className="import-status-badge">
                                  {q.status === 'ready' && '✅'}
                                  {q.status === 'needs_review' && '⚠️'}
                                  {q.status === 'failed' && '❌'}
                                </span>
                              </div>
                              <div className="import-q-meta">
                                <span>{typeLabel(q.type)}</span>
                                {q.options?.length > 0 && <span>{q.options.length} خيارات</span>}
                                <span>{q.points} درجة</span>
                                {q.warnings?.length > 0 && (
                                  <span className="import-warning">{q.warnings[0]}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="import-modal-footer">
                    {!importPreview ? (
                      <>
                        <button className="btn-prev" onClick={closeImportModal} type="button">
                          إلغاء
                        </button>
                        <button
                          className="btn-finish"
                          onClick={handleWordImportSubmit}
                          disabled={!importFile || importLoading}
                          type="button"
                        >
                          {importLoading ? '⏳ جارٍ التحليل...' : '🔍 تحليل الملف'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn-prev" onClick={() => setImportPreview(null)} type="button">
                          <FaArrowRight /> رجوع
                        </button>
                        <button
                          className="btn-finish"
                          onClick={applyImportedQuestions}
                          disabled={importPreview.summary.ready + importPreview.summary.needsReview === 0}
                          type="button"
                        >
                          <FaSave /> إضافة {importPreview.summary.ready + importPreview.summary.needsReview} سؤال للامتحان
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile: Type Picker Modal */}
            {isMobile && typePickerOpen && (
              <div className="modal-overlay" role="dialog" aria-modal="true">
                <div className="modal-sheet">
                  <div className="sheet-header">
                    <h3 style={{ margin: 0 }}>اختر نوع السؤال</h3>
                    <button className="sheet-close" onClick={() => setTypePickerOpen(false)} type="button">
                      <FaTimes />
                    </button>
                  </div>

                  <div className="sheet-body">
                    <button className="sheet-option" onClick={() => addQuestion("mcq")} type="button">
                      <FaListUl /> سؤال اختيارات
                    </button>
                    <button className="sheet-option" onClick={() => addQuestion("true_false")} type="button">
                      <FaCheck /> صح / خطأ
                    </button>
                    <button className="sheet-option" onClick={() => addQuestion("fill_blank")} type="button">
                      <FaAlignLeft /> أكمل (فراغات)
                    </button>
                    <button className="sheet-option" onClick={() => addQuestion("essay")} type="button">
                      <FaAlignLeft /> سؤال مقالي
                    </button>
                    <button
                      className="sheet-option"
                      style={{ borderColor: '#2563eb', color: '#2563eb' }}
                      onClick={() => { setTypePickerOpen(false); setImportModalOpen(true); }}
                      type="button"
                    >
                      <FaFileAlt /> استيراد من Word
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddExamQuestions;
